import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { ProgressSpinner } from "primereact/progressspinner";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import DoughnutChart from "src/components/Charts/DoughnutChart";
import { chartOptions } from "src/resources/constant";
import { RoutePaths, StatusesColors } from "src/resources/enum";
import { formatNumber } from "src/utils/helpers";

interface MaintenanceRequestsProps {}

const MaintenanceRequests: React.FC<MaintenanceRequestsProps> = () => {
  const { t } = useTranslation(["common", "registration"]);
  const navigate = useNavigate();
  const {
    maintenanceRequestsStatistics,
    maintenanceRequestsTotalCount,
    isMaintenanceRequestsStatisticsDataLoaded,
  } = useSelector((state: any) => state.dashboard);
  const [chartsData, setChartsData] = useState<any>();

  useEffect(() => {
    const newChartsData = [
      {
        color: [StatusesColors.Completed, StatusesColors.Planck],
        label: t("completed"),
      },
      {
        color: [StatusesColors.InProgress, StatusesColors.Planck],
        label: t("inProgress"),
      },
      {
        color: [StatusesColors.Exceeded, StatusesColors.Planck],
        label: t("exceeded"),
      },
      {
        color: [StatusesColors.OnHold, StatusesColors.Planck],
        label: t("onHold"),
      },
    ];

    setChartsData(newChartsData);
  }, [
    maintenanceRequestsStatistics,
    maintenanceRequestsTotalCount,
    isMaintenanceRequestsStatisticsDataLoaded,
  ]);
  return (
    <Card className="card">
      <div className="d-flex justify-content-between flex-column pt-2">
        <div className="d-flex justify-content-between align-items-center ">
          <h6 className="card-title">{t("manualMaintenanceRequests")}</h6>
          <Button
            className="edit-btn light-primary-btn m-0"
            onClick={() => {
              navigate(RoutePaths.MaintenanceRequests);
            }}
            label={t("viewRequests")}
          />
        </div>
        <div>
          <span
            className="card-2-1-text-number"
            title={maintenanceRequestsTotalCount}
          >
            {formatNumber(maintenanceRequestsTotalCount)}
          </span>
        </div>
      </div>
      <div className="d-flex flex-column justify-content-between w-100">
        <div className="stats-row maintenance-requests-charts">
          {chartsData &&
            chartsData.length > 1 &&
            (isMaintenanceRequestsStatisticsDataLoaded ? (
              <>
                <div
                  className="card-2-1 w-12"
                  title={maintenanceRequestsStatistics.completed}
                >
                  <DoughnutChart
                    chartData={{
                      labels: [chartsData[0].label, ""],
                      datasets: [
                        {
                          data: [
                            maintenanceRequestsStatistics.completed,
                            maintenanceRequestsTotalCount > 0
                              ? maintenanceRequestsTotalCount -
                                maintenanceRequestsStatistics.completed
                              : 1,
                          ],
                          backgroundColor: chartsData[0].color,
                          borderWidth: 0,
                          borderRadius: 50,
                        },
                      ],
                    }}
                    options={chartOptions}
                    centerText={[
                      {
                        text: formatNumber(
                          maintenanceRequestsStatistics.completed
                        ),
                        fontSize: 24,
                        fontWeight: "bold",
                        xOffset: 0,
                        yOffset: 10,
                      },
                    ]}
                  />
                </div>
                <div
                  className="card-2-1 w-12"
                  title={maintenanceRequestsStatistics.inProgress}
                >
                  <DoughnutChart
                    chartData={{
                      labels: [chartsData[1].label, ""],
                      datasets: [
                        {
                          data: [
                            maintenanceRequestsStatistics.inProgress,
                            maintenanceRequestsTotalCount > 0
                              ? maintenanceRequestsTotalCount -
                                maintenanceRequestsStatistics.inProgress
                              : 1,
                          ],
                          backgroundColor: chartsData[1].color,
                          borderWidth: 0,
                          borderRadius: 50,
                        },
                      ],
                    }}
                    options={chartOptions}
                    centerText={[
                      {
                        text: formatNumber(
                          maintenanceRequestsStatistics.inProgress
                        ),
                        fontSize: 24,
                        fontWeight: "bold",
                        xOffset: 0,
                        yOffset: 10,
                      },
                    ]}
                  />
                </div>
                <div
                  className="card-2-1 w-12"
                  title={maintenanceRequestsStatistics.exceeded}
                >
                  <DoughnutChart
                    chartData={{
                      labels: [chartsData[2].label, ""],
                      datasets: [
                        {
                          data: [
                            maintenanceRequestsStatistics.exceeded,
                            maintenanceRequestsTotalCount > 0
                              ? maintenanceRequestsTotalCount -
                                maintenanceRequestsStatistics.exceeded
                              : 1,
                          ],
                          backgroundColor: chartsData[2].color,
                          borderWidth: 0,
                          borderRadius: 50,
                        },
                      ],
                    }}
                    options={chartOptions}
                    centerText={[
                      {
                        text: formatNumber(
                          maintenanceRequestsStatistics.exceeded
                        ),
                        fontSize: 24,
                        fontWeight: "bold",
                        xOffset: 0,
                        yOffset: 10,
                      },
                    ]}
                  />
                  <div className="stats-row"></div>
                </div>

                <div
                  className="card-2-1 w-12"
                  title={maintenanceRequestsStatistics.onHold}
                >
                  <DoughnutChart
                    chartData={{
                      labels: [chartsData[3].label, ""],
                      datasets: [
                        {
                          data: [
                            maintenanceRequestsStatistics.onHold,
                            maintenanceRequestsTotalCount > 0
                              ? maintenanceRequestsTotalCount -
                                maintenanceRequestsStatistics.onHold
                              : 1,
                          ],
                          backgroundColor: chartsData[3].color,
                          borderWidth: 0,
                          borderRadius: 50,
                        },
                      ],
                    }}
                    options={chartOptions}
                    centerText={[
                      {
                        text: formatNumber(
                          maintenanceRequestsStatistics.onHold
                        ),
                        fontSize: 24,
                        fontWeight: "bold",
                        xOffset: 0,
                        yOffset: 10,
                      },
                    ]}
                  />
                </div>
              </>
            ) : (
              <ProgressSpinner />
            ))}
        </div>

        <div className="stats-row mt-4 maintenance-requests-stats">
          <p className="stats-row__item">
            <span
              className="dot"
              style={{ backgroundColor: StatusesColors.Completed }}
            ></span>
            {t("completed")}
          </p>
          <p className="stats-row__item">
            <span
              className="dot"
              style={{ backgroundColor: StatusesColors.InProgress }}
            ></span>
            {t("inProgress")}
          </p>
          <p className="stats-row__item">
            <span
              className="dot"
              style={{ backgroundColor: StatusesColors.Exceeded }}
            ></span>
            {t("exceeded")}
          </p>
          <p className="stats-row__item">
            <span
              className="dot"
              style={{ backgroundColor: StatusesColors.OnHold }}
            ></span>
            {t("onHold")}
          </p>
        </div>
      </div>
    </Card>
  );
};

export default MaintenanceRequests;
